import { Inbox, Loader2, Sparkles } from "lucide-react";

export function PageHeader({ title, description, action, eyebrow }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          {eyebrow ? <div className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{eyebrow}</div> : null}
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950 md:text-3xl">{title}</h1>
          {description ? <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">{description}</p> : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

export function PremiumCard({ children, className = "" }) {
  return (
    <div className={"card card-hover overflow-hidden " + className}>
      {children}
    </div>
  );
}

export function StatCard({ title, value, icon: Icon, accent = "slate", hint, trend }) {
  const accentMap = {
    slate: "border-slate-200 bg-slate-900 text-white",
    blue: "border-sky-200 bg-sky-50 text-sky-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
  };

  return (
    <PremiumCard className="relative p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-500">{title}</div>
          <div className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">{value}</div>
          {hint ? <div className="mt-2 text-xs text-slate-500">{hint}</div> : null}
        </div>
        {Icon ? (
          <div className={"flex h-11 w-11 items-center justify-center rounded-xl border " + (accentMap[accent] || accentMap.slate)}>
            <Icon className="h-5 w-5" />
          </div>
        ) : null}
      </div>
      {trend ? <div className="mt-4 text-xs font-medium text-slate-600">{trend}</div> : null}
    </PremiumCard>
  );
}

export function EmptyState({ icon: Icon = Inbox, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-slate-900 text-white">
        <Icon className="h-6 w-6" />
      </div>
      <div className="mt-4 text-base font-semibold text-slate-950">{title}</div>
      {description ? <div className="mt-2 max-w-md text-sm leading-6 text-slate-600">{description}</div> : null}
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

export function LoadingCard({ label = "Loading..." }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
      <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
      {label}
    </div>
  );
}

export function StatusBadge({ tone = "slate", children }) {
  const toneMap = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
    violet: "border-violet-200 bg-violet-50 text-violet-700",
  };

  return <span className={"badge font-semibold " + (toneMap[tone] || toneMap.slate)}>{children}</span>;
}

export function TimelineItem({ icon: Icon = Sparkles, tone = "slate", title, meta, description, right }) {
  const toneMap = {
    slate: "bg-slate-900 text-white",
    emerald: "bg-emerald-600 text-white",
    amber: "bg-amber-500 text-white",
    rose: "bg-rose-600 text-white",
    sky: "bg-sky-600 text-white",
    violet: "bg-violet-600 text-white",
  };

  return (
    <div className="relative pl-6">
      <div className={"absolute left-0 top-1 flex h-4 w-4 items-center justify-center rounded-full " + (toneMap[tone] || toneMap.slate)}>
        <Icon className="h-3 w-3" />
      </div>
      <div className="flex items-start justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-colors hover:border-slate-300">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-slate-950">{title}</div>
          {meta ? <div className="mt-1 text-xs text-slate-500">{meta}</div> : null}
          {description ? <div className="mt-2 text-sm text-slate-600">{description}</div> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
    </div>
  );
}
